import React, { useState, useContext, useEffect, useMemo } from 'react';

import { Input, Select } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { isAddressEmpty } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';
import { useUserGuiConfig, useUserGuiConfigDispatch } from '@contexts/UserGuiConfigContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import ContactDropdown from '@components/ContactDropdown/ContactDropdown';
import GooglePlaces from '@components/GooglePlaces/GooglePlaces';
import CustomSwitch from '@components/Gui/CustomSwitch';
import { NwLinkButton } from '@components/Gui/NwButton';
import AddressForm from '@components/Gui/NwForm/AddressForm';
import CustomSelect from '@components/Gui/NwForm/CustomSelect';
import AddressFromContacts from '@components/Gui/NwForm/LocationSelector/AddressFromContacts';
import { NwFakeFormItem } from '@components/Gui/NwForm/NwFakeFormWrapper';
import NwIcon from '@components/Gui/NwIcon';

import AddressesByProject from './AddressesByProject';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const { Option } = Select;

const Wrapper = styled.div`
    .select-type-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 1rem;
    }

    .select-container {
        padding: 20px 0px;
    }

    .google-places-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin: 0 0 8px;
    }

    .under-address-form {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.5rem;
    }

    .new-connection-name-container {
        background-color: ${(props) => props.theme.LighterBorderColor};
        padding: 0.5rem 1rem 0.25rem 1rem;
        border-radius: 4px;
        margin-top: 1rem;

        .nw-form-item-container {
            .ant-form-item {
                .ant-form-item-label {
                    label {
                        color: #000;
                    }
                }
            }
        }
    }

    .address-name-container {
        /* margin-top: 1rem; */
    }

    .ant-input-prefix {
        margin: auto 12px auto 0;
    }

    .ant-input-prefix,
    .select-wrapper > .prefix {
        color: ${(props) => props.theme.PrimaryDarkLight} !important;
        font-size: 0.75rem !important;
        height: 1rem;
        border-right: 1px solid ${(props) => props.theme.DarkerBorderColor};
        padding-right: 0.4rem;
        display: flex;
        justify-content: center;
        text-transform: uppercase;
    }
`;

const searchTypes = {
    project: 'project',
    contact: 'contact',
    google: 'google',
};

const emptyAddress = {
    Name: '',
    Country: '',
    City: '',
    County: '',
    State: '',
    Zip: '',
    Street: '',
    Access: '',
    Phone: '',
};

// i campi che il form può modificare: il confronto ignora ID e metadati della connection
const EDITABLE_ADDRESS_FIELDS = ['Name', 'Street', 'City', 'Zip', 'County', 'State', 'Country', 'Access'];

const isSameAddress = (address, addressToCompare) => {
    if (!address || !addressToCompare) {
        return false;
    }
    return EDITABLE_ADDRESS_FIELDS.every((field) => (address[field] || '') === (addressToCompare[field] || ''));
};

const SEARCH_OPTIONS = [
    {
        value: searchTypes.project,
        label: 'on project',
    },
    {
        value: searchTypes.contact,
        label: 'on contact',
    },
    {
        value: searchTypes.google,
        label: 'on google',
    },
];

const LocationSelector = ({
    eventType = 'event',
    value,
    eventData,
    projectCustomerId,
    customerId,
    onChange,
    projectId,
    onBlur,
    onChangeContactAddress,
    fieldErrors,
    addLocationToCustomer,
    setAddLocationToCustomer,
    clearOnChange,
}) => {
    const availableSearchOptions = useMemo(() => {
        return SEARCH_OPTIONS.filter((option) => option.value !== searchTypes.project || projectId);
    }, [projectId]);

    const defaultContactID = eventData
        ? eventData.EventClientID || eventData.Project.CustomerID || eventData.Project.Contact.ID
        : customerId || projectCustomerId;

    const [selectedOption, setSelectedOption] = useState();
    const [contactId, setContactId] = useState(defaultContactID);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const { cachedList } = useContext(ListContext);
    const countriesList = [...cachedList.countries.items];
    const userGuiConfig = useUserGuiConfig();
    const insertAddress = userGuiConfig?.other?.insertAddressOnEventCreation ?? true;
    const userGuiConfigDispatch = useUserGuiConfigDispatch();
    const [newConnectionName, setNewConnectionName] = useState(() => getContactName(defaultContactID) || '');
    const [showAddLocationToCustomer, setShowAddLocationToCustomer] = useState(true);

    const addressName = useMemo(() => {
        if (!insertAddress && !selectedAddress) {
            return '';
        }
        if (contactId && contactId !== defaultContactID) {
            return getContactName(contactId);
        }
        return '';
    }, [contactId, defaultContactID, insertAddress, selectedAddress]);

    useEffect(() => {
        if (
            insertAddress &&
            addresses.length > 0 &&
            (!value || Object.values(value).every((val) => !val || val === ''))
        ) {
            const defaultAddress = addresses.find((address) => address.IsDefault);

            if (defaultAddress) {
                handleSetAddress(defaultAddress);
                setSelectedAddress(defaultAddress.ID);
            } else {
                handleSetAddress(addresses[0]);
                setSelectedAddress(addresses[0].ID);
            }
        } else {
            if (insertAddress && clearOnChange) {
                handleSetAddress(emptyAddress);
            }
            setSelectedAddress(null);
        }
    }, [addresses]);

    useEffect(() => {
        if (!eventData && customerId) {
            setContactId(customerId);
        }
    }, [eventData, customerId]);

    useEffect(() => {
        setNewConnectionName(getContactName(contactId) || '');
    }, [contactId]);

    const clearAll = () => {
        setAddresses([]);
        setSelectedAddress(null);
        let contactName = '';
        if (contactId && contactId !== defaultContactID) {
            contactName = getContactName(contactId);
        }
        if (clearOnChange) {
            onChange({
                ...emptyAddress,
                Name: contactName,
            });
        }
    };

    const loadContactConnections = async (contactId) => {
        clearAll();
        const defaultConnection = await Axios.get(`/contacts/defaultconnection/${contactId}`);
        const connections = await Axios.get(`/contacts/connections/${contactId}`);
        const contactAddresses = [];
        if (connections && connections.length > 0) {
            for (const connection of connections) {
                if (connection.Addresses.length > 0) {
                    contactAddresses.push(
                        ...connection.Addresses.map((address) => ({
                            ...address,
                            ConnectionID: connection.ID,
                            ConnectionName: connection.Name,
                            ConnectionType: connection.TypeName,
                            IsDefault: defaultConnection && address.ID === defaultConnection.AddressID,
                            Selected: false,
                        }))
                    );
                }
            }
        }
        setAddresses(contactAddresses);
    };

    useEffect(() => {
        if (contactId) {
            loadContactConnections(contactId);
        } else {
            clearAll();
        }
    }, [contactId]);

    // l'indirizzo dell'anagrafica da cui si è partiti: senza ID nel form l'indirizzo è nuovo
    const selectedContactAddress = useMemo(() => {
        if (!value || !value.ID || value.ID === 'add-new') {
            return null;
        }
        return addresses.find((address) => address.ID === value.ID) || null;
    }, [addresses, value]);

    // su un indirizzo già in anagrafica l'aggiornamento ha senso solo se qualcosa è cambiato
    const addressChangedFromContact = useMemo(() => {
        if (!selectedContactAddress) {
            return true;
        }
        return !isSameAddress(value, selectedContactAddress);
    }, [selectedContactAddress, value]);

    // un indirizzo vuoto non va scritto in anagrafica, nemmeno come nuova contact card
    const canSaveAddressOnContact = Boolean(value) && !isAddressEmpty(value) && addressChangedFromContact;

    const addLocationObject = useMemo(() => {
        let contactName = 'customer';
        if (contactId) {
            contactName = getContactName(contactId);
        } else {
            contactName = getContactName(defaultContactID);
        }
        const connectionName = selectedContactAddress ? selectedContactAddress.ConnectionName : '';
        const connectionID = selectedContactAddress ? selectedContactAddress.ConnectionID : null;
        let switchMessage;
        if (!connectionID) {
            switchMessage = (
                <div style={{ whiteSpace: 'pre' }}>
                    <strong>Add</strong> new address to <strong>{contactName}</strong>
                </div>
            );
        } else {
            switchMessage = (
                <div style={{ whiteSpace: 'pre' }}>
                    <strong>Update</strong> address "<em>{connectionName}</em>" on <strong>{contactName}</strong>
                </div>
            );
        }
        const addLocationObjectData = {
            customerID: contactId,
            connectionID: connectionID,
            switchMessage: switchMessage,
            newConnectionName: newConnectionName,
        };
        return addLocationObjectData;
    }, [contactId, selectedContactAddress, newConnectionName]);

    const handleAddLocationToCustomer = (checked) => {
        if (setAddLocationToCustomer) {
            if (checked) {
                setAddLocationToCustomer(addLocationObject);
            } else {
                setAddLocationToCustomer(null);
            }
        }
    };

    useEffect(() => {
        if (addLocationToCustomer && setAddLocationToCustomer) {
            setAddLocationToCustomer(addLocationObject);
        }

    }, [addLocationObject]);

    // tornare all'indirizzo originale annulla l'aggiornamento; un form ancora vuoto no,
    // altrimenti "add a new address" spegnerebbe lo switch che ha appena acceso
    useEffect(() => {
        if (!addressChangedFromContact && addLocationToCustomer) {
            handleAddLocationToCustomer(false);
        }
    }, [addressChangedFromContact]);

    useEffect(() => {
        if (selectedOption && !availableSearchOptions.some((opt) => opt.value === selectedOption)) {
            setSelectedOption(undefined);
        }
    }, [availableSearchOptions, selectedOption]);

    useEffect(() => {
        if ((customerId || projectCustomerId) && !selectedOption) {
            setSelectedOption(searchTypes.contact);
        }
    }, [customerId, projectCustomerId, selectedOption]);

    const handleChangeOption = (value) => {
        if (value !== searchTypes.contact) {
            setSelectedAddress(null);
            setContactId(defaultContactID);
            // si cambia sorgente di ricerca: l'indirizzo scelto prima non vale più
            handleClearAddress();
        }
        if (value === searchTypes.project) {
            setShowAddLocationToCustomer(false);
        } else {
            setShowAddLocationToCustomer(true);
        }
        setSelectedOption(value);
    };

    const handleSetAddress = (address) => {
        if (address.ID) {
            setSelectedAddress(address.ID);
            if (address.ID === 'add-new') {
                handleAddLocationToCustomer(true);
            } else {
                handleAddLocationToCustomer(false);
            }
        } else {
            // google, digitazione o svuotamento: non è più l'indirizzo scelto dall'anagrafica
            setSelectedAddress(null);
        }
        const country = countriesList.find((country) => country.Code === address.CountryCode);
        const newAddress = {
            ...address,
            Name: address.Name || addressName,
        };
        if (country) {
            newAddress.Country = country.Code;
        }
        // il telefono non è editabile nei form ma resta salvato sull'evento: gli indirizzi
        // presi da contatto, progetto o google non lo portano, quindi va conservato com'è
        if (address.Phone === undefined && value && value.Phone) {
            newAddress.Phone = value.Phone;
        }
        onChange(newAddress);
        onChangeContactAddress && onChangeContactAddress(contactId, address);
    };

    const handleChangeContact = async (contactId) => {
        setContactId(contactId);
        setNewConnectionName(getContactName(contactId) || '');
    };

    const handleUpdateInsertAddress = (checked) => {
        const _usergui = {
            ...userGuiConfig,
            other: {
                ...userGuiConfig.other,
                insertAddressOnEventCreation: checked,
            },
        };
        userGuiConfigDispatch({ type: 'Update', value: _usergui });
    };

    const handleClearAddress = () => {
        handleSetAddress(emptyAddress);
        handleAddLocationToCustomer(false);
    };

    const handleAddAddress = () => {
        const newAddress = { ...emptyAddress, ID: 'add-new' };
        handleSetAddress(newAddress);
        setSelectedAddress('add-new');
        onChangeContactAddress && onChangeContactAddress(contactId, newAddress);
    };

    const handleNewConnectionNameChange = (event) => {
        setNewConnectionName(event.target.value);
    };

    return (
        <Wrapper>
            <div className="select-type-container">
                <div>
                    <CustomSwitch
                        size="small"
                        checked={insertAddress}
                        onChange={handleUpdateInsertAddress}
                        text="auto-insert address"
                    />
                </div>
                <CustomSelect
                    autoComplete="off"
                    placeholder="search addresses"
                    value={selectedOption}
                    style={{ width: 240 }}
                    onChange={handleChangeOption}
                    onBlur={onBlur}
                    options={availableSearchOptions}
                />
                {/* {searchOptions.map((searchOption) => (
                        <Option value={searchOption.value} key={searchOption.value}>
                            {searchOption.label}
                        </Option>
                    ))}
                </CustomSelect> */}
            </div>
            <div className="select-container">
                {selectedOption === searchTypes.google && (
                    <div className="google-places-container">
                        <GooglePlaces
                            onSetAddress={handleSetAddress}
                            suffix={<NwIcon icon={byPrefixAndName.fasl['search']} />}
                        />
                    </div>
                )}
                {selectedOption === searchTypes.project && projectId && (
                    <AddressesByProject projectId={projectId} onChange={handleSetAddress} eventData={eventData} />
                )}
                {selectedOption === searchTypes.contact && (
                    <>
                        <ContactDropdown
                            value={contactId}
                            activeTypes={['cli', 'age', 'srv']}
                            onChange={handleChangeContact}
                        />
                        <AddressFromContacts
                            selectedAddress={selectedAddress}
                            addresses={addresses ? addresses : []}
                            onChange={handleSetAddress}
                            onAdd={handleAddAddress}
                        />
                    </>
                )}
                <div>
                    <AddressForm
                        value={value}
                        onChange={handleSetAddress}
                        useAddressName={eventType === 'event' ? true : false}
                        fieldErrors={fieldErrors}
                    />
                    <div className="under-address-form">
                        <NwLinkButton
                            label="Clear Address"
                            onClick={handleClearAddress}
                            icon={byPrefixAndName.fasl['xmark']}
                        />
                        {setAddLocationToCustomer && showAddLocationToCustomer && contactId && (
                            <CustomSwitch
                                checked={addLocationToCustomer}
                                disabled={!canSaveAddressOnContact}
                                onChange={handleAddLocationToCustomer}
                                text={addLocationObject.switchMessage}
                            />
                        )}
                    </div>
                    {setAddLocationToCustomer && addLocationToCustomer && !addLocationObject.connectionID ? (
                        <div className="new-connection-name-container">
                            <NwFakeFormItem
                                label="CONTACT CARD NAME"
                                icon={byPrefixAndName.fasl['address-card']}
                                name="NewConnectionName"
                                control={
                                    <Input
                                        value={newConnectionName}
                                        name="NewConnectionName"
                                        onChange={handleNewConnectionNameChange}
                                    />
                                }
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </Wrapper>
    );
};

export default LocationSelector;
